import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, RefreshCcw, AlertCircle, Check, X, AlertTriangle, Copy, Activity } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import ProfileSowSection from './components/ProfileSowSection';
import {
  fetchKycEntriesForMember,
  fetchIncidentsForMember,
  fetchMonitorAlertsForMember,
} from './components/MemberCompliancePanels';
import PatronGamingActivityPanel from "../../components/PatronGamingActivityPanel";
import ProfileDetailHeader from './components/profile-v2/ProfileDetailHeader';
import ProfileSummaryCards from './components/profile-v2/ProfileSummaryCards';
import ProfileSectionNav, { scrollToSection } from './components/profile-v2/ProfileSectionNav';
import ProfileSectionAnchor from './components/profile-v2/ProfileSectionAnchor';
import ProfileNeedsAttention from './components/profile-v2/ProfileNeedsAttention';
import ProfilePatronDetailsCard from './components/profile-v2/ProfilePatronDetailsCard';
import ProfileRiskSection from './components/profile-v2/ProfileRiskSection';
import ProfileComplianceTimeline from './components/profile-v2/ProfileComplianceTimeline';
import ProfileScreeningSection from './components/profile-v2/ProfileScreeningSection';
import ProfileIdentityDocuments from './components/profile-v2/ProfileIdentityDocuments';
import { buildComplianceTimeline } from './components/profile-v2/buildComplianceTimeline';
import { buildNeedsAttention } from './components/profile-v2/buildNeedsAttention';
import { collectIdentityDocuments } from './components/profile-v2/documentFieldUtils';
import { getApiUrl, getServerName, getSystemID, getLocalApiUrl, authFetch } from '../../config/api';
import ProfileAmlMatchReviewPanel from '../../components/aml/ProfileAmlMatchReviewPanel';
import { FormsModuleGate, RecordFormsPanel } from '../forms';
import {
  detectOcrNameMismatch,
  parseRiskFactorsList,
  resolveAmlHeaderStatus,
  resolveEffectiveRisk,
  riskAssessmentLooksStale,
} from '../../lib/profileDetailConsistency';
import { flattenResultsByCategory, normalizeProfileDob } from '../../utils/amlResultCategories';
import FoldIntoModal from './components/FoldIntoModal';

function parseRiskFactors(raw) {
  return parseRiskFactorsList(raw);
}

function getDetailFromList(list, fieldName) {
  if (!Array.isArray(list)) return '';
  const row = list.find((r) => String(r?.Name || r?.name || '').toLowerCase() === String(fieldName).toLowerCase());
  const val = row?.Content ?? row?.content;
  return typeof val === 'string' && val.trim() ? val.trim() : '';
}

function profileDocumentLookupId(profileData, profileId) {
  const candidates = [profileData?.MasterProfileID, profileData?.MID, profileId].filter(Boolean)
  return candidates.find((c) => String(c).trim()) || null
}

function ProfileDocumentViewLink({ profileKey, document }) {
  const [opening, setOpening] = React.useState(false)
  if (!document?.id || !document?.source || !profileKey) {
    if (document?.image_available) {
      return <Badge className="bg-green-100 text-green-800 border-green-200">Available</Badge>
    }
    return <Badge className="bg-gray-100 text-gray-800 border-gray-200">No file</Badge>
  }
  const openFile = async (e) => {
    e.preventDefault()
    setOpening(true)
    try {
      const url = getLocalApiUrl(
        `/api/profile/${encodeURIComponent(String(profileKey))}/documents/${encodeURIComponent(String(document.id))}/download?source=${encodeURIComponent(document.source)}`
      )
      const res = await authFetch(url)
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || `HTTP ${res.status}`)
      }
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120000)
    } catch (err) {
      alert(err.message || 'Could not open document file')
    } finally {
      setOpening(false)
    }
  }
  return (
    <button
      type="button"
      onClick={openFile}
      disabled={opening}
      className="text-psg-teal text-xs font-semibold hover:underline disabled:opacity-60"
    >
      {opening ? 'Opening…' : 'View file'}
    </button>
  )
}

/** When dedicated photo B64 fields are empty, reuse licence image from OCR pages (data URLs). */
function firstOcrDataUrl(ocr) {
  if (!Array.isArray(ocr)) return '';
  for (const p of ocr) {
    const u = p?.PageURL ?? p?.pageURL;
    if (typeof u === 'string' && u.trim().startsWith('data:')) return u.trim();
  }
  return '';
}

const getAmlCategoryFromSample = (sample) => {
  if (!Array.isArray(sample) || sample.length === 0) return null;
  const firstWithCategory = sample.find((r) => r && (r.riskCategory || r.category));
  return (firstWithCategory?.riskCategory || firstWithCategory?.category || '').toString() || null;
};

// Helper function to format dates consistently
const formatDateOnly = (dateTimeStr) => {
  if (!dateTimeStr) return '—';
  const s = String(dateTimeStr).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d} ${months[Number(m) - 1]} ${y}`;
  }
  try {
    const date = new Date(s);
    if (Number.isNaN(date.getTime())) return s;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate().toString().padStart(2, '0')} ${months[date.getMonth()]} ${date.getFullYear()}`;
  } catch {
    return s;
  }
};

function dobToInputValue(raw) {
  if (!raw) return ''
  const s = String(raw).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const parsed = Date.parse(s)
  if (Number.isNaN(parsed)) return ''
  return new Date(parsed).toISOString().slice(0, 10)
}

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return 'Unknown';
  
  try {
    const date = new Date(dateTimeStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    // Check if the original string contains time information
    const hasTime = dateTimeStr.includes('T') || dateTimeStr.includes(':') || dateTimeStr.includes(' ');
    
    if (hasTime && (date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0)) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
    } else {
      return `${day} ${month} ${year}`;
    }
  } catch {
    return dateTimeStr; // Return as-is if parsing fails
  }
};

const ProfileDetail = ({ profileId, onBack, onOpenProfile }) => {
  const { toast } = useToast();
  const { can } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [patronProfile, setPatronProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [membershipData, setMembershipData] = useState(null);
  const [isLoadingMembership, setIsLoadingMembership] = useState(false);
  const [showMembershipPanel, setShowMembershipPanel] = useState(false);
  const [isRunningAmlCheck, setIsRunningAmlCheck] = useState(false);
  const [amlChecks, setAmlChecks] = useState([]);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [isAmlModalOpen, setIsAmlModalOpen] = useState(false);
  const [amlModalData, setAmlModalData] = useState(null);
  const [amlReviewSaving, setAmlReviewSaving] = useState(false);
  const [amlHeaderStatus, setAmlHeaderStatus] = useState(null);
  const [amlHeaderHint, setAmlHeaderHint] = useState(null);
  const [showGamingPanel, setShowGamingPanel] = useState(false);
  const [caseHistory, setCaseHistory] = useState([]);
  const [isLoadingCases, setIsLoadingCases] = useState(false);
  const [kycEntries, setKycEntries] = useState([]);
  const [isLoadingKyc, setIsLoadingKyc] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [monitorAlerts, setMonitorAlerts] = useState([]);
  const [isLoadingIncidents, setIsLoadingIncidents] = useState(false);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);
  const [sowStatus, setSowStatus] = useState(null);
  const [riskRecalculating, setRiskRecalculating] = useState(false);
  const [riskBreakdown, setRiskBreakdown] = useState(null);
  const [riskBreakdownLoading, setRiskBreakdownLoading] = useState(false);
  const [selectedRiskDomain, setSelectedRiskDomain] = useState(null);
  const [isContributingFactorsExpanded, setIsContributingFactorsExpanded] = useState(false);
  const [patronOverviewLoaded, setPatronOverviewLoaded] = useState(false);
  const autoRiskAttemptedRef = useRef(null);
  const staleRiskRefreshRef = useRef(null);
  const [cddRefreshKey, setCddRefreshKey] = useState(0);
  // undefined = not yet fetched · null = fetched but unclassified · string = classified tier
  const [headerCddTier, setHeaderCddTier] = useState(undefined);
  const [contactEditOpen, setContactEditOpen] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editMiddleName, setEditMiddleName] = useState('');
  const [editDob, setEditDob] = useState('');
  const [contactSaving, setContactSaving] = useState(false);
  const [foldOpen, setFoldOpen] = useState(false);
  const [foldStatus, setFoldStatus] = useState(null);
  const canEditProfile = can('profile-safe/search', 'edit');

  // Copy to clipboard function
  const copyToClipboard = (text, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: 'Copied to clipboard',
        description: `Profile ID: ${text}`,
        duration: 2000,
      });
    }).catch(() => {
      toast({
        title: 'Copy failed',
        description: 'Unable to copy to clipboard',
        variant: 'destructive',
        duration: 2000,
      });
    });
  };

  const copyProfileLink = (e) => {
    e?.stopPropagation?.()
    const id = profileData?.MasterProfileID || profileData?.MembershipNumber || profileData?.MID || profileId
    const params = new URLSearchParams()
    if (id && /^PAT-/i.test(String(id))) params.set('masterProfileId', String(id))
    else if (id) params.set('memberId', String(id))
    const path = params.toString() ? `/profile-safe/search?${params.toString()}` : '/profile-safe/search'
    const url = `${window.location.origin}${path}`
    navigator.clipboard.writeText(url).then(() => {
      toast({
        title: 'Copied profile link',
        description: 'Opening this URL loads the same patron.',
        duration: 2000,
      })
    }).catch(() => {
      toast({
        title: 'Copy failed',
        description: 'Unable to copy profile link',
        variant: 'destructive',
        duration: 2000,
      })
    })
  }

  // Removed fetchFileData function - now using SmartImage for OCR images

  // Add to Watchlist (person of interest)
  const addToWatchlist = async () => {
    try {
      const staffMemberId = profileData?.MembershipNumber || profileData?.MID || String(profileId)
      const firstName = profileData?.FN || ''
      const lastName = profileData?.LN || ''
      const dob = profileData?.DOB || ''
      const payload = {
        member_id: staffMemberId,
        alert_type: 'POI',
        severity: 'medium',
        status: 'open',
        notes: `Added from Profile detail${firstName || lastName ? ` — ${[firstName, lastName].filter(Boolean).join(' ')}` : ''}${dob ? ` (DOB ${dob})` : ''}`,
      }
      const resp = await authFetch(getLocalApiUrl('/api/watchlist'), { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify(payload) })
      const json = await resp.json()
      if (!resp.ok || !json.success) throw new Error(json.error || 'Failed to add to watchlist')
      toast({ title: 'Added to Watchlist', description: `${staffMemberId} — ${firstName} ${lastName}` })
    } catch (e) {
      toast({ title: 'Add to Watchlist failed', description: e.message || 'Unknown error', variant: 'destructive' })
    }
  }

  // Function to fetch membership details - uses patron/resolve API (same format as payout mobile).
  // Prefer club membership # (MembershipNumber). MID from GetProfile is IGT player id — wrong for PiG MemberID.
  const fetchMembershipDetails = async () => {
    const membershipNumber = String(profileData?.MembershipNumber || '').trim()
    const playerId = String(profileData?.MID || '').trim()
    const resolveKey = membershipNumber || playerId
    if (!resolveKey) {
      toast({
        title: "Error",
        description: "Membership number not found",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingMembership(true);
    
    try {
      // Use the patron/resolve API (same response format as mobile payout)
      const response = await authFetch(getLocalApiUrl('/api/patron/resolve'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: resolveKey,
          lookup_type: membershipNumber ? 'membership_number' : 'member_id'
        })
      });
      
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }

      const json = await response.json();
      console.log('Patron Resolve Response:', json);

      if (!json.success || !json.data?.resolved || !json.data?.patron) {
        toast({
          title: 'Membership details',
          description: json.data?.message || 'No membership record found for this profile.',
          variant: 'destructive'
        });
        setMembershipData(null);
        setShowMembershipPanel(true);
        return;
      }

      // Map patron/resolve data to PatronDetails format for display
      const patron = json.data.patron;
      const mappedData = {
        ResponseCode: '200',
        ResponseMessage: 'Success',
        VersionNo: '1.0',
        PatronDetails: {
          MemberID: json.data.membership_number || json.data.member_id || resolveKey,
          MasterProfileID: json.data.master_profile_id || profileData?.MasterProfileID,
          AccountID: profileData?.AID || '',
          FirstName: patron.firstname || profileData?.FN || '',
          LastName: patron.lastname || profileData?.LN || '',
          DateOfBirth: normalizeProfileDob(patron.dob || profileData?.DOB || ''),
          Gender: patron.gender || '',
          Occupation: patron.occupation || '',
          Email: patron.email || '',
          MobilePhone: patron.mobile_phone || '',
          Address1: patron.address1 || '',
          Address2: patron.address2 || '',
          Suburb: patron.suburb || '',
          State: patron.state || '',
          PostCode: patron.postal_code || '',
          TierLevel: patron.tier_level || '',
          MembershipStatus: patron.status || 'ACTIVE',
          MembershipType: json.data.patron_kind?.toUpperCase() || 'MEMBER',
          HomeSite: '',
          DateJoined: patron.join_date || '',
          LastVisitDate: '',
          IDDocumentType:
            patron.id_document_type ||
            getDetailFromList(profileData?.CreatedDocumentDetails, 'DocumentType') ||
            getDetailFromList(profileData?.RecentDocumentDetails, 'DocumentType') ||
            '',
          IDDocumentNumber:
            patron.id_document_number ||
            getDetailFromList(profileData?.CreatedDocumentDetails, 'DocumentNumber') ||
            getDetailFromList(profileData?.RecentDocumentDetails, 'DocumentNumber') ||
            '',
          CountryofResidence: patron.country_of_residence || patron.country || '',
          CountryofCitizenship:
            patron.nationality ||
            patron.country_of_citizenship ||
            (Array.isArray(patron.citizenship_countries)
              ? patron.citizenship_countries.filter(Boolean).join(', ')
              : '') ||
            '',
          CitizenshipMode: patron.citizenship_mode || '',
          CitizenshipCountries: Array.isArray(patron.citizenship_countries)
            ? patron.citizenship_countries
            : [],
          CardNumber: json.data.visitor_id || json.data.card_id || '',
          RiskScore: patron.risk_score,
          RiskLevel: patron.risk_level,
          OnWatchlist: json.data.on_watchlist,
          WatchlistAlert: json.data.watchlist_alert,
        }
      };

      console.log('Mapped Membership Data:', mappedData);
      setMembershipData(mappedData);
      setShowMembershipPanel(true);

    } catch (error) {
      console.error('Membership Details Error:', error);
      setMembershipData(null);
      toast({
        title: 'Membership details failed',
        description: error?.message || 'Could not load membership details',
        variant: 'destructive'
      });
      setShowMembershipPanel(true);
    } finally {
      setIsLoadingMembership(false);
    }
  };

  const openAmlResultsModal = (payload) => {
    const flat = flattenResultsByCategory(payload.results, payload.resultsByCategory);
    setAmlModalData({
      ...payload,
      results: flat.length > 0 ? flat : payload.results || []
    });
    setIsAmlModalOpen(true);
  };

  // Run AML/PEP check: strict (DOB) first, then relax to name+country, then name-only when no results
  const runAmlPepCheck = async () => {
    if (!profileData) return;
    const getDetailValue = (list, fieldName) => {
      if (!Array.isArray(list)) return null;
      const row = list.find(r => (r?.Name || '').toLowerCase() === fieldName.toLowerCase());
      const val = row?.Content;
      return typeof val === 'string' && val.trim().length ? val.trim() : null;
    };
    const getDocNumber = (list) => getDetailValue(list, 'DocumentNumber');
    const issueCountry = getDetailValue(profileData.CreatedDocumentDetails, 'IssueCountry')
      || getDetailValue(profileData.RecentDocumentDetails, 'IssueCountry')
      || null;
    const fallbackCountry = issueCountry || undefined;
    const toCountryCode = (country) => {
      if (!country) return '';
      const c = country.trim().toUpperCase();
      const map = {
        'AUSTRALIA': 'AUST',
        'UNITED STATES': 'USA',
        'UNITED STATES OF AMERICA': 'USA',
        'UNITED KINGDOM': 'UK',
        'NEW ZEALAND': 'NZ',
        'PAKISTAN': 'PAKIS',
        'IRAN': 'IRAN',
        'INDIA': 'INDIA',
        'CHINA': 'CHINA'
      };
      if (map[c]) return map[c];
      const letters = c.replace(/[^A-Z]/g, '');
      return letters.slice(0, 5);
    };
    try {
      setIsRunningAmlCheck(true);
      const createdId = getDocNumber(profileData.CreatedDocumentDetails);
      const recentId = getDocNumber(profileData.RecentDocumentDetails);
      const docIds = [createdId, recentId].filter(Boolean);
      const documents = { document_ids: docIds };
      const patronDob = normalizeProfileDob(profileData.DOB);
      const countryCode = toCountryCode(fallbackCountry);
      const memberPayload = {
        // Prefer club membership # (same key used when listing history). MID is IGT player id.
        member_id:
          String(profileData.MembershipNumber || '').trim() ||
          String(profileData.MID || '').trim() ||
          String(profileId || '').trim(),
        name: `${profileData.FN || ''} ${profileData.LN || ''}`.trim(),
        performed_by: 'web_user'
      };

      const postAmlCheck = async (fields, reason, opts = {}) => {
        const resp = await authFetch(`${getLocalApiUrl('/api/aml/checks/run')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...memberPayload,
            ...fields,
            reason,
            // Intermediate fallbacks only write history when a match is found;
            // the final attempt always persists (clear or match) so one Run = one row.
            ...(opts.persist_only_if_matches ? { persist_only_if_matches: true } : {}),
            ...(opts.persist === false ? { persist: false } : {}),
          })
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data?.success) {
          throw new Error(data?.error || 'AML check failed');
        }
        return data;
      };

      const finishCheck = async (responseData, toastTitle) => {
        const count = responseData?.data?.result_count ?? 0;
        const isClear = count === 0 || responseData?.data?.screening_clear === true;
        if (isClear) {
          toast({
            title: 'Screening clear',
            description: 'No PEP · No Sanctions · No adverse media. Risk and CDD will refresh.',
          });
        } else {
          toast({ title: toastTitle, description: `Matches found: ${count}` });
        }
        await fetchAmlChecks();
        openAmlResultsModal({
          checkId: Number(responseData?.data?.id),
          name: String(memberPayload.name),
          country: countryCode || undefined,
          results: Array.isArray(responseData?.data?.sample) ? responseData.data.sample : [],
          resultsByCategory: responseData?.data?.resultsByCategory || null,
          provider: responseData?.data?.provider,
          greenidVerificationId: responseData?.data?.greenidVerificationId,
          greenidOverallStatus: responseData?.data?.greenidOverallStatus,
          screeningSummary: responseData?.data?.screeningSummary || null
        });
        try {
          const cat = getAmlCategoryFromSample(
            Array.isArray(responseData?.data?.sample) ? responseData.data.sample : []
          );
          setAmlHeaderStatus(cat || (isClear ? 'CLEAR' : 'MATCH'));
        } catch { /* ignore */ }

        // Backend recalculates risk/CDD after the screen; refresh local cards from that result
        // (or fall back to explicit recalculate if the payload omitted scores).
        const d = responseData?.data || {};
        if (d.risk_score != null || d.risk_level || d.cdd_tier) {
          setProfileData((prev) => prev ? {
            ...prev,
            RiskScore: d.risk_score ?? prev.RiskScore,
            RiskLevel: d.risk_level ?? prev.RiskLevel,
            CddTier: d.cdd_tier ?? prev.CddTier,
            RiskAssessedAt: new Date().toISOString(),
          } : prev);
          if (d.components && d.weights) {
        setRiskBreakdown({ components: d.components, weights: d.weights });
      }

      if (d.cdd_tier) setHeaderCddTier(d.cdd_tier);
          setCddRefreshKey((k) => k + 1);
          setPatronProfile((prev) => prev ? {
            ...prev,
            risk_score: d.risk_score ?? prev.risk_score,
            risk_level: d.risk_level ?? prev.risk_level,
            cdd_tier: d.cdd_tier ?? prev.cdd_tier,
          } : prev);
        } else {
          try { await handleRecalculateRisk(); } catch { /* non-fatal */ }
        }
        return count;
      };

      const strictFields = {
        country: countryCode || undefined,
        date_of_birth: patronDob || undefined
      };

      // Waterfall: tighten → relax. Intermediate clears do not write history rows.
      if (docIds.length > 0) {
        const withDoc = await postAmlCheck(
          { ...strictFields, documents },
          'Profile detail on-demand AML/PEP check (with document)',
          { persist_only_if_matches: true }
        );
        if ((withDoc?.data?.result_count ?? 0) > 0) {
          await finishCheck(withDoc, 'AML/PEP Check Completed');
          return;
        }
      }

      // Name + country + DOB
      const strict = await postAmlCheck(
        strictFields,
        'Profile detail on-demand AML/PEP check',
        { persist_only_if_matches: true }
      );
      if ((strict?.data?.result_count ?? 0) > 0) {
        await finishCheck(strict, 'AML/PEP Check Completed');
        return;
      }

      // Name + country (no DOB — Dow Jones records often lack DOB)
      const nameCountry = await postAmlCheck(
        { country: countryCode || undefined },
        'Fallback AML/PEP check (name+country) after no matches with DOB',
        { persist_only_if_matches: true }
      );
      if ((nameCountry?.data?.result_count ?? 0) > 0) {
        await finishCheck(nameCountry, 'AML/PEP Check Completed (Fallback)');
        return;
      }

      // Name only — final attempt always persisted (clear or match)
      const nameOnly = await postAmlCheck(
        {},
        'Fallback AML/PEP check (name only) after no matches with country'
      );
      await finishCheck(nameOnly, 'AML/PEP Check Completed (Fallback)');
    } catch (e) {
      toast({ title: 'AML/PEP Check Failed', description: e.message || 'Unknown error', variant: 'destructive' });
    } finally {
      setIsRunningAmlCheck(false);
    }
  };

  // Load AML check history for this member (membership # preferred; API expands player/master ids).
  const fetchAmlChecks = async () => {
    const memberId =
      String(profileData?.MembershipNumber || '').trim() ||
      String(profileData?.MID || '').trim() ||
      String(profileId || '').trim();
    if (!memberId) return;
    try {
      const resp = await authFetch(getLocalApiUrl(`/api/aml/checks?member_id=${encodeURIComponent(memberId)}&limit=25`));
      const data = await resp.json();
      if (data && data.success) setAmlChecks(data.data || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { fetchAmlChecks(); }, [profileData?.MembershipNumber, profileData?.MID, profileId]);

  // Fetch ECDD/SMR case history when profile has Member ID
  useEffect(() => {
    const memberId = profileData?.MembershipNumber || profileData?.MID;
    if (!memberId) {
      setCaseHistory([]);
      return;
    }
    const fetchCases = async () => {
      setIsLoadingCases(true);
      try {
        const res = await authFetch(getLocalApiUrl(`/api/cases?member_id=${encodeURIComponent(memberId)}&limit=50`));
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          const relevant = json.data.filter((c) =>
            ['SMR', 'ARCHIVAL_SMR', 'ECDD', 'SUSPICIOUS', 'INVESTIGATION'].includes(String(c.case_type || '').toUpperCase())
          );
          setCaseHistory(relevant);
        } else {
          setCaseHistory([]);
        }
      } catch (_) {
        setCaseHistory([]);
      } finally {
        setIsLoadingCases(false);
      }
    };
    fetchCases();
  }, [profileData?.MembershipNumber, profileData?.MID]);

  // KYC registry entries (club membership # and player id when both exist)
  useEffect(() => {
    const keys = [
      profileData?.MembershipNumber,
      profileData?.MID,
    ].filter((k) => k != null && String(k).trim())
    if (keys.length === 0) {
      setKycEntries([]);
      return;
    }
    let cancelled = false;
    const loadKyc = async () => {
      setIsLoadingKyc(true);
      try {
        const rows = await fetchKycEntriesForMember(keys);
        if (!cancelled) setKycEntries(rows);
      } catch (_) {
        if (!cancelled) setKycEntries([]);
      } finally {
        if (!cancelled) setIsLoadingKyc(false);
      }
    };
    loadKyc();
    return () => { cancelled = true; };
  }, [profileData?.MembershipNumber, profileData?.MID]);

  // UAR / incident reports + monitor alerts (UAR and incident report are the same incidents table)
  useEffect(() => {
    const lookupId = profileData?.MembershipNumber || profileData?.MID;
    if (!lookupId) {
      setIncidents([]);
      setMonitorAlerts([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoadingIncidents(true);
      setIsLoadingAlerts(true);
      try {
        const [incRows, alertRows] = await Promise.all([
          fetchIncidentsForMember(lookupId),
          fetchMonitorAlertsForMember(lookupId),
        ]);
        if (!cancelled) {
          setIncidents(incRows);
          setMonitorAlerts(alertRows);
        }
      } catch (_) {
        if (!cancelled) {
          setIncidents([]);
          setMonitorAlerts([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingIncidents(false);
          setIsLoadingAlerts(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [profileData?.MembershipNumber, profileData?.MID]);

  // Load profile documents (identity_documents + onboarding + KYC attachments)
  useEffect(() => {
    const loadDocs = async () => {
      const candidates = [
        profileData?.MasterProfileID,
        profileData?.MID,
        profileId,
      ].filter(Boolean);
      const id = candidates.find((c) => String(c).trim()) || null;
      if (!id) return;
      try {
        const resp = await authFetch(
          getLocalApiUrl(`/api/profile/${encodeURIComponent(String(id))}/documents`)
        );
        const data = await resp.json();
        if (data && data.success) setOtherDocuments(data.data || []);
      } catch (_) {
        setOtherDocuments([]);
      }
    };
    if (!profileData) return;
    loadDocs();
  }, [profileData?.MasterProfileID, profileData?.MID, profileId]);

  // Open AML check modal from URL param (?aml_check_id=123)
  useEffect(() => {
    const url = new URL(window.location.href);
    const idParam = url.searchParams.get('aml_check_id');
    if (idParam) {
      const idNum = Number(idParam);
      if (!Number.isNaN(idNum)) {
        void viewAmlCheckDetails(idNum);
      }
    }
  }, []);

  // Helper: View details of a specific AML check
  const viewAmlCheckDetails = async (checkId) => {
    try {
      const resp = await authFetch(getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(String(checkId))}`))
      const data = await resp.json();
      if (resp.ok && data?.success && data.data) {
        const row = data.data || {};
        const results = Array.isArray(row.result_sample) ? row.result_sample : [];
        openAmlResultsModal({
          checkId,
          name: String(row.full_name || ''),
          country: String(row.country_code || ''),
          results,
          analystDecision: row.analyst_decision || null,
          analystReason: row.analyst_reason || '',
        });
        return;
      }
      // Fallback: if backend details not found (e.g., 404), try re-running an advanced search from listing metadata
      const meta = amlChecks.find(c => c.id === checkId);
      if (!meta || !meta.full_name) throw new Error(data?.error || 'Check not found');
      const params = new URLSearchParams({
        name: meta.full_name,
        country: meta.country_code || '',
        includeInactive: 'true',
        limit: '50'
      });
      const resp2 = await authFetch(getLocalApiUrl(`/api/aml/search/persons/advanced?${params}`));
      const data2 = await resp2.json();
      if (!resp2.ok) throw new Error(data2?.error || 'Failed to search for results');
      const results = (data2 && data2.data && (Array.isArray(data2.data.results) ? data2.data.results : data2.data)) || [];
      openAmlResultsModal({
        checkId,
        name: String(meta.full_name),
        country: meta.country_code || '',
        results: Array.isArray(results) ? results : [],
        resultsByCategory: data2?.data?.resultsByCategory || null,
        provider: data2?.data?.provider,
        greenidVerificationId: data2?.data?.greenidVerificationId,
        greenidOverallStatus: data2?.data?.greenidOverallStatus,
        screeningSummary: data2?.data?.screeningSummary || null
      });
    } catch (e) {
      toast({ title: 'Unable to load AML Check', description: e.message || 'Unknown error', variant: 'destructive' });
    }
  };

  // AML header: summarise material hits (confirmed adverse media, etc.), not only latest row.
  useEffect(() => {
    const resolved = resolveAmlHeaderStatus(amlChecks)
    setAmlHeaderStatus(resolved.status)
    setAmlHeaderHint(resolved.hint)
  }, [amlChecks]);

  const effectiveRisk = useMemo(
    () => resolveEffectiveRisk(profileData, patronProfile),
    [profileData, patronProfile]
  )

  const ocrNameMismatch = useMemo(
    () => (profileData ? detectOcrNameMismatch(profileData) : null),
    [profileData]
  )

  const latestKycSummary = useMemo(() => {
    if (!kycEntries.length) return null
    const latest = kycEntries[0]
    const reason = String(latest.reason || 'KYC').replace(/_/g, ' ')
    const dvs = latest.dvs_status || latest.dvs_result || latest.outcome
    return { reason, dvs, id: latest.id }
  }, [kycEntries])


  useEffect(() => {
    const fetchProfileData = async () => {
      setIsLoading(true);
      
      try {
        // Load from API
        console.log('Loading data from API');
        
        const requestBody = {
          ServerName: getServerName(),
          SystemID: getSystemID(),
          Purpose: "GETPROFILE",
          MasterProfileID: profileId,
          Source: "RXPROFILEWEB",
          DeviceType: "RXPROFILEWEB"
        };
        
        console.log('API Request Body:', requestBody);
        
        const response = await fetch(getApiUrl('GET_PROFILE_DETAILS'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
        
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
        
        // Get the raw response text first to check for JSON parsing issues
        const responseText = await response.text();
        console.log('Raw API Response:', responseText);
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON Parse Error:', parseError);
          console.error('Response that failed to parse:', responseText);
          throw new Error(`Invalid JSON response from server: ${parseError instanceof Error ? parseError.message : 'Unknown parsing error'}`);
        }
        
        console.log('API Response:', data);
        
        if (data.ResponseCode !== '00' && data.ResponseCode !== '0') {
          throw new Error(`API Error (${data.ResponseCode}): ${data.ResponseMessage}`);
        }
        
        const profile = data.OutputData[0];
        if (!profile) {
          throw new Error('No profile data found in API response');
        }
        
        console.log('Successfully loaded data from API');
        
        // Just use the profile data as-is from the API
        // The backend now includes OCRDetails with base64 images
        setProfileData(profile);
        
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setProfileData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [profileId]);

  // Fetch additional patron data (visitor_id, profile_type) from our backend
  useEffect(() => {
    let cancelled = false
    setPatronOverviewLoaded(false)
    setPatronProfile(null)
    const fetchPatronProfile = async () => {
      const lookupId = profileData?.MID || profileData?.MasterProfileID || profileId
      if (!lookupId) {
        if (!cancelled) setPatronOverviewLoaded(true)
        return
      }
      try {
        const resp = await authFetch(getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(lookupId)}/overview`))
        const data = await resp.json()
        if (cancelled) return
        if (resp.ok && data?.success && data?.data) {
          const d = data.data
          setPatronProfile({
            ...(d.profile || {}),
            compliance_stage: d.compliance_stage,
            compliance_stage_updated_at: d.compliance_stage_updated_at,
            cdd_tier: d.cdd_tier ?? d.profile?.cdd_tier,
            risk_score: d.risk_score ?? d.profile?.risk_score,
            risk_level: d.risk_level ?? d.profile?.risk_level,
            risk_assessed_at: d.risk_assessed_at ?? d.profile?.risk_assessed_at,
          })
        }
      } catch (_) {
        // Non-critical: profile may not exist in patron_profiles yet
      } finally {
        if (!cancelled) setPatronOverviewLoaded(true)
      }
    }
    if (profileData) fetchPatronProfile()
    return () => { cancelled = true }
  }, [profileData?.MID, profileData?.MasterProfileID, profileId])

  useEffect(() => {
    let cancelled = false
    const loadFold = async () => {
      const id = profileData?.MasterProfileID || profileId
      if (!id) {
        setFoldStatus(null)
        return
      }
      try {
        const resp = await authFetch(getLocalApiUrl(`/api/profiles/${encodeURIComponent(id)}/fold-status`))
        const json = await resp.json().catch(() => ({}))
        if (cancelled) return
        if (resp.ok && json.success) setFoldStatus(json.data)
        else setFoldStatus(null)
      } catch {
        if (!cancelled) setFoldStatus(null)
      }
    }
    if (profileData) loadFold()
    return () => { cancelled = true }
  }, [profileData?.MasterProfileID, profileId])

  // New profile navigation — allow one auto-calc attempt for this detail view.
  useEffect(() => {
    autoRiskAttemptedRef.current = null
    staleRiskRefreshRef.current = null
    setPatronOverviewLoaded(false)
    setSowStatus(null)
  }, [profileId])

  // headerCddTier is set via onTierLoaded callback from CddTierCard — single source of truth

  const openContactEdit = () => {
    setEditFirstName(String(profileData?.FN || patronProfile?.first_name || '').trim())
    setEditLastName(String(profileData?.LN || patronProfile?.last_name || '').trim())
    setEditMiddleName(String(profileData?.MN || patronProfile?.middle_name || '').trim())
    setEditDob(dobToInputValue(profileData?.DOB || patronProfile?.date_of_birth))
    setContactEmail(String(profileData?.Email || patronProfile?.email || '').trim())
    setContactPhone(String(profileData?.Phone || patronProfile?.phone || '').trim())
    setContactEditOpen(true)
  }

  const saveContactEdit = async () => {
    const lookupId =
      profileData?.MasterProfileID ||
      profileData?.MembershipNumber ||
      profileData?.MID ||
      profileId
    if (!lookupId) {
      toast({ title: 'Cannot update profile', description: 'No profile id', variant: 'destructive' })
      return
    }
    const first = editFirstName.trim()
    const last = editLastName.trim()
    if (!first || !last) {
      toast({ title: 'Name required', description: 'First and last name cannot be empty.', variant: 'destructive' })
      return
    }
    setContactSaving(true)
    try {
      const emailTrim = contactEmail.trim()
      const phoneTrim = contactPhone.trim()
      const middleTrim = editMiddleName.trim()
      const dobTrim = editDob.trim()
      const res = await authFetch(
        getLocalApiUrl(`/api/profiles/${encodeURIComponent(String(lookupId))}/current-state`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: first,
            last_name: last,
            middle_name: middleTrim,
            date_of_birth: dobTrim || null,
            email: emailTrim,
            phone: phoneTrim,
            clearMiddleName: !middleTrim,
            clearDob: !dobTrim,
            clearEmail: !emailTrim,
            clearPhone: !phoneTrim,
          }),
        }
      )
      const j = await res.json().catch(() => ({}))
      if (!res.ok || !j.success) {
        throw new Error(j.error || 'Failed to update profile')
      }
      const dobOut = j.data?.dateOfBirth
        ? String(j.data.dateOfBirth).slice(0, 10)
        : null
      setProfileData((prev) =>
        prev
          ? {
              ...prev,
              FN: j.data?.firstName ?? first,
              LN: j.data?.lastName ?? last,
              MN: j.data?.middleName ?? middleTrim,
              DOB: dobOut || prev.DOB,
              Email: j.data?.email ?? null,
              Phone: j.data?.phone ?? null,
            }
          : prev
      )
      setPatronProfile((prev) =>
        prev
          ? {
              ...prev,
              first_name: j.data?.firstName ?? first,
              last_name: j.data?.lastName ?? last,
              middle_name: j.data?.middleName ?? null,
              date_of_birth: dobOut,
              email: j.data?.email ?? null,
              phone: j.data?.phone ?? null,
            }
          : prev
      )
      setContactEditOpen(false)
      toast({
        title: 'Profile updated',
        description: j.data?.unchanged
          ? 'No changes to save.'
          : j.data?.note || 'Current profile saved. The next membership ingest may overwrite name and date of birth.',
      })
    } catch (e) {
      toast({
        title: 'Update failed',
        description: e.message || 'Could not save profile',
        variant: 'destructive',
      })
    } finally {
      setContactSaving(false)
    }
  }

  const handleRecalculateRisk = useCallback(async (opts = {}) => {
    const silent = Boolean(opts?.silent)
    const lookupId =
      profileData?.MasterProfileID ||
      profileData?.MembershipNumber ||
      profileData?.MID ||
      profileId
    if (!lookupId) return
    setRiskRecalculating(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/profiles/${encodeURIComponent(lookupId)}/recalculate-risk`),
        { method: 'POST' }
      )
      const json = await res.json()
      if (!res.ok || !json.success) {
        throw new Error(json.error || json.message || 'Failed to recalculate risk')
      }
      const d = json.data || {}

      // Reload from server so header matches investigation / watchlist (canonical row).
      try {
        const overviewRes = await authFetch(
          getLocalApiUrl(`/api/watchlist/member/${encodeURIComponent(lookupId)}/overview`)
        )
        const overviewJson = await overviewRes.json()
        if (overviewRes.ok && overviewJson?.success && overviewJson?.data) {
          const od = overviewJson.data
          setPatronProfile({
            ...(od.profile || {}),
            compliance_stage: od.compliance_stage,
            compliance_stage_updated_at: od.compliance_stage_updated_at,
            cdd_tier: od.cdd_tier ?? od.profile?.cdd_tier,
            risk_score: od.risk_score ?? od.profile?.risk_score,
            risk_level: od.risk_level ?? od.profile?.risk_level,
            risk_assessed_at: od.risk_assessed_at ?? od.profile?.risk_assessed_at,
          })
        }
      } catch (_) { /* non-fatal */ }

      let refreshedProfile = null
      const detailsRes = await fetch(getApiUrl('GET_PROFILE_DETAILS'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ServerName: getServerName(),
          SystemID: getSystemID(),
          Purpose: 'GETPROFILE',
          MasterProfileID: profileData?.MasterProfileID || profileId,
          Source: 'RXPROFILEWEB',
          DeviceType: 'RXPROFILEWEB',
        }),
      })
      if (detailsRes.ok) {
        const detailsJson = await detailsRes.json()
        refreshedProfile = detailsJson?.OutputData?.[0] || null
        if (refreshedProfile) setProfileData(refreshedProfile)
      }

      if (!refreshedProfile) {
        setProfileData((prev) => prev ? {
          ...prev,
          RiskScore: d.risk_score ?? prev.RiskScore,
          RiskLevel: d.risk_level ?? prev.RiskLevel,
          RiskCategory: d.risk_category ?? prev.RiskCategory,
          CddTier: d.cdd_tier ?? prev.CddTier,
          RiskFactors: d.risk_factors ?? prev.RiskFactors,
          RiskAssessedAt: new Date().toISOString(),
        } : prev)
      }

      if (d.cdd_tier) setHeaderCddTier(d.cdd_tier)
      setCddRefreshKey((k) => k + 1)
      const cddDesc = d.cdd_tier ? ` · CDD: ${d.cdd_tier}` : ''
      toast({
        title: silent ? 'Risk calculated' : 'Risk assessment updated',
        description: `${d.risk_level || '—'} · ${d.risk_score ?? '—'}/100${cddDesc}`,
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: silent ? 'Could not calculate risk' : 'Could not recalculate risk',
        description: err?.message || 'Unknown error',
      })
    } finally {
      setRiskRecalculating(false)
    }
  }, [profileData?.MasterProfileID, profileData?.MembershipNumber, profileData?.MID, profileId, toast])

  // First open (or never assessed): GetProfile maps null DB risk → 0/LOW, so auto-calculate once.
  useEffect(() => {
    if (!profileData || !patronOverviewLoaded || riskRecalculating) return
    const key = String(
      profileData.MasterProfileID || profileData.MembershipNumber || profileData.MID || profileId || ''
    ).trim()
    if (!key || autoRiskAttemptedRef.current === key) return

    const assessedAt =
      profileData.RiskAssessedAt ||
      patronProfile?.risk_assessed_at ||
      null
    const rawScore = patronProfile?.risk_score ?? profileData.RiskScore
    const scoreNum =
      rawScore == null || rawScore === '' ? null : Number(rawScore)
    // GetProfile maps null DB risk → 0/LOW; only auto-calc when never assessed.
    const needsCalc = !assessedAt || scoreNum == null

    if (!needsCalc) return
    autoRiskAttemptedRef.current = key
    handleRecalculateRisk({ silent: true })
  }, [
    profileData,
    patronProfile,
    patronOverviewLoaded,
    riskRecalculating,
    profileId,
    handleRecalculateRisk,
  ])

  // Refresh saved risk_factors when compliance activity post-dates last assessment.
  useEffect(() => {
    if (!profileData || !patronOverviewLoaded || riskRecalculating) return
    const key = String(
      profileData.MasterProfileID || profileData.MembershipNumber || profileData.MID || profileId || ''
    ).trim()
    if (!key) return
    const stale = riskAssessmentLooksStale(profileData, patronProfile, { amlChecks, kycEntries })
    if (!stale || staleRiskRefreshRef.current === key) return
    staleRiskRefreshRef.current = key
    handleRecalculateRisk({ silent: true })
  }, [
    profileData,
    patronProfile,
    patronOverviewLoaded,
    amlChecks,
    kycEntries,
    riskRecalculating,
    profileId,
    handleRecalculateRisk,
  ])

  useEffect(() => {
    const lookupId =
      profileData?.MasterProfileID || profileData?.MID || profileId
    if (!lookupId) {
      setSowStatus(null)
      return
    }
    let cancelled = false
    authFetch(
      getLocalApiUrl(`/api/profiles/${encodeURIComponent(String(lookupId))}/source-of-wealth`)
    )
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return
        if (json?.success && json?.data?.current) setSowStatus('on_file')
        else setSowStatus('missing')
      })
      .catch(() => {
        if (!cancelled) setSowStatus(null)
      })
    return () => {
      cancelled = true
    }
  }, [profileData?.MasterProfileID, profileData?.MID, profileId])

  const riskLookupId =
    profileData?.MasterProfileID ||
    profileData?.MembershipNumber ||
    profileData?.MID ||
    profileId

  useEffect(() => {
    if (!riskLookupId || riskRecalculating) return
    let cancelled = false
    setRiskBreakdownLoading(true)
    authFetch(getLocalApiUrl(`/api/profiles/${encodeURIComponent(riskLookupId)}/risk-score`))
      .then((res) => res.json())
      .then((json) => {
        if (cancelled || !json?.success || !json?.data) return
        setRiskBreakdown({
          components: json.data.components,
          weights: json.data.weights,
        })
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setRiskBreakdownLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [riskLookupId, riskRecalculating, profileData?.RiskAssessedAt])

  useEffect(() => {
    setSelectedRiskDomain(null)
    setRiskBreakdown(null)
    setIsContributingFactorsExpanded(false)
  }, [riskLookupId])

  useEffect(() => {
    if (selectedRiskDomain) setIsContributingFactorsExpanded(true)
  }, [selectedRiskDomain])

  const sortedOtherDocuments = useMemo(
    () => [...otherDocuments].sort((a, b) => new Date(b.captured_at || 0).getTime() - new Date(a.captured_at || 0).getTime()),
    [otherDocuments]
  )

  const complianceTimeline = useMemo(
    () =>
      buildComplianceTimeline({
        kycEntries,
        amlChecks,
        caseHistory,
        incidents,
        monitorAlerts,
      }),
    [kycEntries, amlChecks, caseHistory, incidents, monitorAlerts]
  )

  const needsAttentionItems = useMemo(
    () =>
      buildNeedsAttention({
        profileData,
        caseHistory,
        kycEntries,
        amlChecks,
        ocrNameMismatch,
        sowStatus,
      }),
    [profileData, caseHistory, kycEntries, amlChecks, ocrNameMismatch, sowStatus]
  )

  const identityDocuments = useMemo(
    () =>
      collectIdentityDocuments({
        profileData,
        otherDocuments: sortedOtherDocuments,
        ocrNameMismatch,
      }),
    [profileData, sortedOtherDocuments, ocrNameMismatch]
  )

  const openInvestigation = useMemo(() => {
    return (caseHistory || []).find((c) => {
      const s = String(c.status || '').toLowerCase()
      return s !== 'closed' && s !== 'filed' && s !== 'resolved'
    })
  }, [caseHistory])

  const handleSectionNavigate = useCallback((section, item) => {
    setActiveSection(section)
    if (item?.amlCheckId) {
      void viewAmlCheckDetails(item.amlCheckId)
    }
  }, [])

  const handleCopyAmlLink = useCallback(
    (checkId) => {
      const url = new URL(window.location.href)
      url.searchParams.set('aml_check_id', String(checkId))
      navigator.clipboard.writeText(url.toString())
      toast({ title: 'Link copied', description: `AML check #${checkId} link copied to clipboard` })
    },
    [toast]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCcw className="h-12 w-12 animate-spin text-primary" />
          <p className="text-lg font-medium">Loading profile data...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="p-6">
        <Button variant="outline" onClick={onBack} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Search
        </Button>
        <Card>
          <CardContent className="py-10">
            <div className="flex flex-col items-center justify-center">
              <AlertTriangle className="h-16 w-16 text-red-500 mb-4" />
              <p className="text-xl font-semibold text-gray-900">Failed to Load Profile</p>
              <p className="text-sm text-gray-500 mt-2">
                Could not load profile data from API. An error popup was shown with details.
              </p>
              <div className="mt-4 space-x-2">
                <Button variant="outline" onClick={() => window.location.reload()}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
                <Button onClick={onBack}>Return to Search</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ocrDataUrlFallback = firstOcrDataUrl(profileData.OCRDetails);
  const verifyB64Display = (profileData.VerifyFPPhotoB64 || '').trim() || ocrDataUrlFallback;

  const headerCdd =
    headerCddTier !== undefined
      ? headerCddTier || profileData.CddTier || patronProfile?.cdd_tier
      : profileData.CddTier || patronProfile?.cdd_tier;

  const dvsSubtitle = kycEntries[0]
    ? `${formatDateTime(kycEntries[0].performed_at || kycEntries[0].created_at)}${latestKycSummary?.dvs ? ` · ${String(latestKycSummary.dvs).replace(/_/g, ' ')}` : ''}`
    : profileData.DVSStatus
      ? 'Profile DVS status on file'
      : 'No KYC checks on record';

  const onSectionNavigate = (section, item) => {
    scrollToSection(section)
    if (item?.amlCheckId) {
      void viewAmlCheckDetails(item.amlCheckId)
    }
  }

  const openMembershipSection = () => {
    void fetchMembershipDetails()
  }

  return (
    <div className="min-h-full flex flex-col bg-slate-100">
      <ProfileDetailHeader
        profileData={profileData}
        patronProfile={patronProfile}
        verifyB64Display={verifyB64Display}
        onBack={onBack}
        onCopyId={copyToClipboard}
        onCopyLink={copyProfileLink}
        onAddToWatchlist={addToWatchlist}
        onRunAml={runAmlPepCheck}
        isRunningAml={isRunningAmlCheck}
        onOpenMembership={openMembershipSection}
        isLoadingMembership={isLoadingMembership}
        onOpenGaming={() => setShowGamingPanel(true)}
        onEditProfile={openContactEdit}
        canEditProfile={canEditProfile}
        onFoldProfile={() => setFoldOpen(true)}
        canFoldProfile={canEditProfile && foldStatus?.fold_available !== false}
        foldStatus={foldStatus}
        onOpenKeeper={(id) => onOpenProfile?.(id)}
      />

      <ProfileSummaryCards
        complianceStage={patronProfile?.compliance_stage}
        cddTier={headerCdd}
        effectiveRisk={effectiveRisk}
        openCaseLabel={
          openInvestigation
            ? `${openInvestigation.case_number || openInvestigation.id} open`
            : null
        }
        dvsStatus={profileData.DVSStatus || 'Unknown'}
        dvsSubtitle={dvsSubtitle}
        amlStatus={amlHeaderStatus || 'N/A'}
        amlSubtitle={amlHeaderHint}
      />

      <div className="grid grid-cols-[200px_1fr] flex-1 items-start max-lg:grid-cols-1">
        <ProfileSectionNav observeKey={profileId} />

        <div className="px-6 py-5 pb-12 min-w-0 space-y-2">
          <ProfileSectionAnchor id="overview">
            <div className="grid grid-cols-2 gap-4 max-xl:grid-cols-1">
              <ProfileNeedsAttention items={needsAttentionItems} onNavigate={onSectionNavigate} />
              <ProfilePatronDetailsCard
                profileData={profileData}
                patronProfile={patronProfile}
                formatDateTime={formatDateTime}
                onEditContact={openContactEdit}
                canEditProfile={canEditProfile}
              />
            </div>
          </ProfileSectionAnchor>

          <ProfileSectionAnchor id="risk">
            <ProfileRiskSection
              profileData={profileData}
              profileId={profileId}
              effectiveRisk={effectiveRisk}
              riskBreakdown={riskBreakdown}
              riskBreakdownLoading={riskBreakdownLoading}
              riskRecalculating={riskRecalculating}
              selectedRiskDomain={selectedRiskDomain}
              onSelectRiskDomain={setSelectedRiskDomain}
              isContributingFactorsExpanded={isContributingFactorsExpanded}
              onToggleContributingFactors={() => setIsContributingFactorsExpanded((v) => !v)}
              cddRefreshKey={cddRefreshKey}
              onTierLoaded={setHeaderCddTier}
              onRecalculateRisk={() => handleRecalculateRisk()}
              parseRiskFactors={parseRiskFactors}
            />
          </ProfileSectionAnchor>

          <ProfileSectionAnchor id="timeline">
            <ProfileComplianceTimeline
              events={complianceTimeline}
              onViewAmlCheck={viewAmlCheckDetails}
            />
          </ProfileSectionAnchor>

          <ProfileSectionAnchor id="screening">
            <ProfileScreeningSection
              amlChecks={amlChecks}
              amlHeaderStatus={amlHeaderStatus}
              amlHeaderHint={amlHeaderHint}
              isRunningAml={isRunningAmlCheck}
              onRunAml={runAmlPepCheck}
              onViewAmlCheck={viewAmlCheckDetails}
              onCopyAmlLink={handleCopyAmlLink}
            />
          </ProfileSectionAnchor>

          <ProfileSectionAnchor id="identity">
            {ocrNameMismatch && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-950">
                  <p className="font-semibold m-0">Document name does not match profile</p>
                  <p className="mt-1 mb-0">
                    {ocrNameMismatch.source} OCR shows <strong>{ocrNameMismatch.ocrName}</strong> but this profile is{' '}
                    <strong>{ocrNameMismatch.profileName}</strong>. Review whether the correct person was linked.
                  </p>
                </div>
              </div>
            )}
            <ProfileIdentityDocuments
              documents={identityDocuments}
              profileDocumentViewLink={(document) => (
                <ProfileDocumentViewLink
                  profileKey={profileDocumentLookupId(profileData, profileId)}
                  document={document}
                />
              )}
            />
          </ProfileSectionAnchor>

          <ProfileSectionAnchor id="declarations">
            <div className="[&_.border-l-4]:border-l-0">
              <ProfileSowSection
                masterProfileId={profileData?.MasterProfileID || profileData?.MID || profileId}
              />
            </div>
            <FormsModuleGate>
              <div className="mt-4">
                <RecordFormsPanel
                  subjectType="patron"
                  subjectId={String(profileData?.MasterProfileID || profileData?.MID || profileId || '')}
                  subjectLabel={[profileData?.FirstName, profileData?.LastName].filter(Boolean).join(' ') || 'This patron'}
                  patronId={String(profileData?.MasterProfileID || profileData?.MID || profileId || '')}
                  emailTo={String(profileData?.Email || patronProfile?.email || '').trim()}
                  smsTo={String(profileData?.MobilePhone || profileData?.Phone || patronProfile?.phone || '').trim()}
                  placement="profile.extra"
                  readOnly={!can('compliance-safe/forms', 'assign')}
                  title="Additional forms"
                />
              </div>
            </FormsModuleGate>
          </ProfileSectionAnchor>
        </div>
      </div>

      {/* AML Results Right Panel — same list/filter/detail UX as AML Screening */}
      {isAmlModalOpen && amlModalData && typeof document !== 'undefined' && createPortal(
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[100]"
            onClick={() => { setIsAmlModalOpen(false); }}
            aria-hidden="true"
          />

          <div className="fixed right-0 top-0 h-full w-[min(1100px,100vw)] bg-white shadow-xl z-[110] transform transition-transform duration-300 ease-in-out flex flex-col">
            <div className="flex items-center justify-between p-4 border-b shrink-0">
              <div>
                <h3 className="text-lg font-semibold">
                  {(!amlModalData.results || amlModalData.results.length === 0)
                    ? 'Screening clear'
                    : 'AML/PEP match review'}
                </h3>
                <div className="text-sm text-gray-600">
                  {amlModalData.name || 'Patron'}
                  {amlModalData.checkId ? ` · Check #${amlModalData.checkId}` : ''}
                  {amlModalData.country ? ` · ${amlModalData.country}` : ''}
                  {Array.isArray(amlModalData.results) && amlModalData.results.length > 0
                    ? ` · ${amlModalData.results.length} result${amlModalData.results.length === 1 ? '' : 's'}`
                    : ''}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIsAmlModalOpen(false); }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {(!amlModalData.results || amlModalData.results.length === 0) ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center">
                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <Check className="h-7 w-7 text-green-700" />
                    </div>
                    <div className="text-lg font-semibold text-green-900">No matches found</div>
                    <p className="mt-1 text-sm text-green-800">
                      Screening completed with a clear result for this patron.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                      <span className="inline-flex items-center rounded-full border border-green-300 bg-white px-3 py-1 text-sm font-medium text-green-800">
                        No PEP
                      </span>
                      <span className="inline-flex items-center rounded-full border border-green-300 bg-white px-3 py-1 text-sm font-medium text-green-800">
                        No Sanctions
                      </span>
                      <span className="inline-flex items-center rounded-full border border-green-300 bg-white px-3 py-1 text-sm font-medium text-green-800">
                        No Adverse Media
                      </span>
                    </div>
                  </div>
                  {amlChecks.filter((c) => c.result_count > 0).length > 0 && (
                    <div className="text-sm text-blue-700 border border-blue-200 rounded-lg p-3 bg-blue-50">
                      Earlier checks on this profile did return matches. Close this and click <strong>View</strong> on those rows if you need to review them.
                    </div>
                  )}
                </div>
              ) : (
                <ProfileAmlMatchReviewPanel
                  results={amlModalData.results}
                  searchName={amlModalData.name || ''}
                  checkId={amlModalData.checkId || null}
                  initialDecision={amlModalData.analystDecision || null}
                  initialReason={amlModalData.analystReason || ''}
                  saving={amlReviewSaving}
                  onSaveReview={async ({ decision, reason, selected_match }) => {
                    if (!amlModalData.checkId) throw new Error('Check id missing')
                    setAmlReviewSaving(true)
                    try {
                      const resp = await authFetch(
                        getLocalApiUrl(`/api/aml/checks/${encodeURIComponent(String(amlModalData.checkId))}/review`),
                        {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ decision, reason, selected_match }),
                        }
                      )
                      const data = await resp.json().catch(() => ({}))
                      if (!resp.ok || !data?.success) {
                        throw new Error(data?.error || 'Failed to save review')
                      }
                      setAmlModalData((prev) => prev ? {
                        ...prev,
                        analystDecision: decision,
                        analystReason: reason || '',
                      } : prev)
                      if (decision === 'no_match') {
                        setAmlHeaderStatus('CLEAR')
                        toast({
                          title: 'Recorded — no match',
                          description: 'None of these hits were linked to this patron.',
                        })
                      } else {
                        setAmlHeaderStatus('MATCH')
                        toast({
                          title: 'Match confirmed',
                          description: 'Selected hit saved on this screening check.',
                        })
                      }
                      await fetchAmlChecks()
                      try { await handleRecalculateRisk(); } catch { /* non-fatal */ }
                    } finally {
                      setAmlReviewSaving(false)
                    }
                  }}
                />
              )}
            </div>
          </div>
        </>,
        document.body
      )}

    {/* Membership Details Sliding Panel */}
    {showMembershipPanel && (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setShowMembershipPanel(false)}
        />
        
        {/* Panel */}
        <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
          {/* Panel Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold">Membership Details</h3>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowMembershipPanel(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Panel Content */}
          <div className="p-4 overflow-y-auto" style={{ height: 'calc(100vh - 80px)' }}>
            {membershipData && membershipData.PatronDetails ? (
              <div className="space-y-4">
                {/* Member Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between">
                      Member Details
                      {membershipData.PatronDetails.MembershipStatus && (
                        <Badge className={`text-xs border ${
                          String(membershipData.PatronDetails.MembershipStatus).toLowerCase() === 'financial'
                            ? 'bg-green-100 text-green-800 border-green-200'
                            : String(membershipData.PatronDetails.MembershipStatus).toLowerCase() === 'lapsed'
                            ? 'bg-amber-100 text-amber-900 border-amber-200'
                            : String(membershipData.PatronDetails.MembershipStatus).toLowerCase() === 'archived'
                            ? 'bg-slate-100 text-slate-600 border-slate-300'
                            : 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}>
                          {membershipData.PatronDetails.MembershipStatus}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Personal Information */}
                    <div>
                      <h5 className="font-medium text-gray-900 mb-2">Personal Information</h5>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Name:</span>
                          <p className="font-medium">{membershipData.PatronDetails.FirstName} {membershipData.PatronDetails.LastName}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Date of Birth:</span>
                          <p className="font-medium">{formatDateOnly(membershipData.PatronDetails.DateOfBirth)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Gender:</span>
                          <p className="font-medium">{membershipData.PatronDetails.Gender === 'M' ? 'Male' : membershipData.PatronDetails.Gender === 'F' ? 'Female' : membershipData.PatronDetails.Gender}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Occupation:</span>
                          <p className="font-medium">{membershipData.PatronDetails.Occupation || 'Not specified'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Membership Information */}
                    <div className="pt-3 border-t">
                      <h5 className="font-medium text-gray-900 mb-2">Membership Information</h5>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Member #:</span>
                          <p className="font-medium">{membershipData.PatronDetails.MemberID}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Account ID:</span>
                          <p className="font-medium">{membershipData.PatronDetails.AccountID}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Card Number:</span>
                          <p className="font-medium">{membershipData.PatronDetails.CardNumber}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Membership Type:</span>
                          <p className="font-medium">{membershipData.PatronDetails.MembershipType || <span className="text-gray-400">—</span>}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Home Site:</span>
                          <p className="font-medium">{membershipData.PatronDetails.HomeSite}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Joined Date:</span>
                          <p className="font-medium">{formatDateTime(membershipData.PatronDetails.DateJoined)}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Last Visit:</span>
                          <p className="font-medium">{membershipData.PatronDetails.LastVisitDate ? formatDateTime(membershipData.PatronDetails.LastVisitDate) : 'Never'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="pt-3 border-t">
                      <h5 className="font-medium text-gray-900 mb-2">Contact Information</h5>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-600">Email:</span>
                          <p className="font-medium">{membershipData.PatronDetails.Email}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Mobile Phone:</span>
                          <p className="font-medium">{membershipData.PatronDetails.MobilePhone}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Address:</span>
                          <p className="font-medium">
                            {membershipData.PatronDetails.Address1}
                            {membershipData.PatronDetails.Address2 && <><br />{membershipData.PatronDetails.Address2}</>}
                            <br />
                            {membershipData.PatronDetails.Suburb}, {membershipData.PatronDetails.State} {membershipData.PatronDetails.PostCode}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* ID Document Information */}
                    <div className="pt-3 border-t">
                      <h5 className="font-medium text-gray-900 mb-2">ID Document</h5>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-600">Document Type:</span>
                          <p className="font-medium">{membershipData.PatronDetails.IDDocumentType}</p>
                        </div>
                        <div>
                          <span className="text-gray-600">Document Number:</span>
                          <p className="font-medium">{membershipData.PatronDetails.IDDocumentNumber}</p>
                        </div>
                        {membershipData.PatronDetails.CountryofResidence && (
                          <div>
                            <span className="text-gray-600">Country of Residence:</span>
                            <p className="font-medium">{membershipData.PatronDetails.CountryofResidence}</p>
                          </div>
                        )}
                        {membershipData.PatronDetails.CountryofCitizenship && (
                          <div>
                            <span className="text-gray-600">Country of Citizenship:</span>
                            <p className="font-medium">{membershipData.PatronDetails.CountryofCitizenship}</p>
                          </div>
                        )}
                        {membershipData.PatronDetails.CitizenshipMode && (
                          <div>
                            <span className="text-gray-600">Citizenship declaration:</span>
                            <p className="font-medium">
                              {membershipData.PatronDetails.CitizenshipMode === 'multiple'
                                ? 'More than one'
                                : membershipData.PatronDetails.CitizenshipMode === 'single'
                                  ? 'Single'
                                  : membershipData.PatronDetails.CitizenshipMode}
                            </p>
                          </div>
                        )}
                        {Array.isArray(membershipData.PatronDetails.CitizenshipCountries) &&
                          membershipData.PatronDetails.CitizenshipCountries.length > 0 && (
                          <div className="col-span-2">
                            <span className="text-gray-600">Citizenship countries:</span>
                            <p className="font-medium">
                              {membershipData.PatronDetails.CitizenshipCountries.join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No membership data available</p>
                {isLoadingMembership && <p className="mt-2">Loading...</p>}
              </div>
            )}
          </div>
        </div>
      </>
    )}

    {contactEditOpen && (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-slate-200">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900 m-0">Edit profile</h3>
            <button
              type="button"
              className="text-slate-400 hover:text-slate-600"
              onClick={() => setContactEditOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-4 py-3 space-y-3">
            <p className="text-xs text-slate-500 m-0">
              Saves the current profile row only. The next DimPatron ingest may overwrite name and
              date of birth. Changes are audited.
            </p>
            <div className="grid grid-cols-3 gap-3 max-sm:grid-cols-1">
              <label className="block">
                <span className="text-xs font-medium text-slate-700">First name</span>
                <input
                  type="text"
                  className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Middle name</span>
                <input
                  type="text"
                  className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                  value={editMiddleName}
                  onChange={(e) => setEditMiddleName(e.target.value)}
                  placeholder="Optional"
                  autoComplete="off"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-700">Last name</span>
                <input
                  type="text"
                  className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  autoComplete="off"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-xs font-medium text-slate-700">Date of birth</span>
              <input
                type="date"
                className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                value={editDob}
                onChange={(e) => setEditDob(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-700">Email</span>
              <input
                type="email"
                className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="patron@example.com"
                autoComplete="off"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-700">Phone</span>
              <input
                type="tel"
                className="mt-1 w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+614xxxxxxxx"
                autoComplete="off"
              />
            </label>
            <p className="text-[11px] text-slate-400 m-0">Leave a field blank to clear it.</p>
          </div>
          <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100">
            <Button type="button" variant="ghost" size="sm" onClick={() => setContactEditOpen(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={contactSaving} onClick={saveContactEdit}>
              {contactSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    )}

    <FoldIntoModal
      open={foldOpen}
      onClose={() => setFoldOpen(false)}
      currentMasterProfileId={profileData?.MasterProfileID || profileId}
      currentName={`${profileData?.FN || ''} ${profileData?.LN || ''}`.trim()}
      onFolded={(data) => {
        const keeper = data?.target?.master_profile_id
        toast({
          title: 'Profiles folded',
          description: `${data?.source?.master_profile_id} is now hidden inside ${keeper}.`,
        })
        if (keeper && onOpenProfile) onOpenProfile(keeper)
      }}
    />

    {/* Transaction & Gaming Profile Modal */}
    {showGamingPanel && profileData && (
      <PatronGamingActivityPanel
        memberId={profileData.MembershipNumber || profileData.MID || String(profileId)}
        memberName={`${profileData.FN} ${profileData.LN}`}
        onClose={() => setShowGamingPanel(false)}
      />
    )}
  </div>
  );
};

export default ProfileDetail;